import { gql } from '@apollo/client';
import type { ErrorLike } from '@apollo/client';

/**
 * `atlasEgressIps` — the wire behind Cluster access's two answer tiles.
 *
 * One row per cluster × egress address: who stands behind the address, and which
 * Atlas slot it holds. The TABLE this used to feed was removed on 2026-08-26 (owner
 * decision); what still needs the whole fleet in one answer is the pair of tiles —
 * how many slots silent addresses hold, and which clusters report an address that
 * reaches no list — plus the Overview's reclaimable line. None of those can be
 * assembled per project, which is why the query stays.
 *
 * Nothing in this module recomputes a verdict. `addressLive` and `reclaimable` are
 * READ from the server — see D6 and AC-9. If a field looks derivable, it is
 * deliberately not derived.
 */

export interface AtlasEgressIpProject {
  projectKey: string;
  /** null = the list has NEVER been read · false = read and absent · true = read and present. */
  inLiveList?: boolean | null;
  /** THE selection predicate. `state ∈ ATLAS_RECLAIMABLE_STATES && addressLive === false`. */
  reclaimable: boolean;
}

export interface AtlasEgressIp {
  cidr: string;
  clusterName?: string | null;
  agentsTotal: number;
  /** The D6 verdict: true iff ANY agent behind this address is alive. Never per agent. */
  addressLive: boolean;
  projects: AtlasEgressIpProject[];
}

/**
 * SELECTED DOWN to what the two tiles and the Overview's reclaimable line actually
 * read. The per-address fields the removed table rendered — `nodeNames`,
 * `agentsLive`/`agentsGone`, `lastSeenAt`, and the per-project `projectDisplayName`,
 * `reported`, `state`, `stateAt` — came off the document with it: this query is
 * unpaged over the whole fleet (458 rows in production), so a field nothing renders is
 * paid for on every Overview and Cluster access load. They are still on the server;
 * re-add the ones a new surface needs, and only those.
 */
export const ATLAS_EGRESS_IPS = gql`
  query AtlasEgressIps {
    atlasEgressIps {
      cidr
      clusterName
      agentsTotal
      addressLive
      projects {
        projectKey
        inLiveList
        reclaimable
      }
    }
  }
`;

/**
 * D11 — the backend merges FIRST, and the web bundle deploys ahead of the api
 * rollout on every merge (`deploy:cf-pages:*` needs only `build:web:*`). So for a
 * few minutes, or for the whole of a rollback, this bundle can be talking to an api
 * that has never heard of `atlasEgressIps`.
 *
 * That is a GraphQL VALIDATION failure, not a data error: the whole document is
 * rejected before execution. The two surfaces that read it must degrade to "not
 * available yet" — the tiles print an em dash, the Overview drops one checklist line
 * — while the capacity band, the drawers and the nav, which read other queries, keep
 * working. An error boundary here would take the whole screen down for a transient
 * deploy skew.
 *
 * Matched on the field name rather than on a message template, because the wording
 * of "Cannot query field" is the server's to change and the field name is ours.
 */
export function isEgressQueryUnavailable(error: ErrorLike | undefined): boolean {
  if (!error) return false;
  // Apollo 4 wraps this two different ways depending on the HTTP status, and the
  // one that actually happens is the SECOND: our api answers a validation failure
  // with 400, so HttpLink raises a ServerError whose `message` is only "Response
  // not successful: Received status code 400" and whose payload is on `result` /
  // `bodyText`. Reading `message` alone silently misses it — which it did, until a
  // browser run against a stubbed 400 showed the generic "API didn't respond"
  // panel instead of this one. Both shapes are checked, plus the plain
  // CombinedGraphQLErrors case (200 with an `errors` array).
  const e = error as {
    message?: string;
    errors?: { message?: string }[];
    result?: unknown;
    bodyText?: string;
  };
  const parts: string[] = [e.message ?? '', e.bodyText ?? ''];
  for (const g of e.errors ?? []) parts.push(g?.message ?? '');
  if (e.result != null) {
    try {
      parts.push(typeof e.result === 'string' ? e.result : JSON.stringify(e.result));
    } catch {
      // A payload that will not serialise tells us nothing; the other parts still do.
    }
  }
  return parts.some((m) => m.includes('atlasEgressIps') || m.includes('AtlasEgressIp'));
}
